import { authApi } from '@/api/endpoints/auth'
import { wabaApi } from '@/api/endpoints/waba'
import { queryKeys } from '@/api/query-keys'
import { queryClient } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { useWabaStore } from '@/stores/wabaStore'

let bootstrapPromise: Promise<void> | null = null

/** Clear in-flight bootstrap (call on logout). */
export function resetSessionBootstrap(): void {
  bootstrapPromise = null
}

/**
 * Load permissions + WABA list once per session.
 * Concurrent callers share the same in-flight request (login + AppLayout + StrictMode).
 */
export async function bootstrapSession(options?: { force?: boolean }): Promise<void> {
  if (bootstrapPromise && !options?.force) {
    return bootstrapPromise
  }

  bootstrapPromise = (async () => {
    const [permsResult, mapResult, wabaResult] = await Promise.allSettled([
      authApi.getMyPermissions(),
      authApi.getPermissionMap(),
      wabaApi.getWabaList(),
    ])

    if (permsResult.status === 'fulfilled') {
      usePermissionStore.getState().setAllPermissionList(permsResult.value.permissions)
      // Keep the admin bypass in sync each session — the login value can be
      // stale (older logins computed it with a narrower check).
      useAuthStore.getState().updateUser({
        permissions: permsResult.value.permissions,
        is_company_admin: permsResult.value.is_admin,
      })
    }
    if (mapResult.status === 'fulfilled') {
      usePermissionStore.getState().setCodenameToId(mapResult.value)
    }
    if (wabaResult.status === 'fulfilled') {
      const res = wabaResult.value
      useWabaStore.getState().setWabaData(
        res.data ?? [],
        res.default_waba_id != null ? String(res.default_waba_id) : null,
        res.has_waba,
      )
      queryClient.setQueryData(queryKeys.wabaList, res)
    }
  })()

  try {
    await bootstrapPromise
  } catch (error) {
    bootstrapPromise = null
    throw error
  }
}
